import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, useMediaQuery } from '@mui/material';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Card from '@/components/Card';
import Page from '@/components/Page';
import Paginator from '@/components/Paginator/Paginator';
import Stack from '@/components/Stack';
import Table from '@/components/Table';
import TableHeader from '@/components/TableHeader';
import { Body, Small } from '@/components/Typography';
import {
  WorkersV2API,
  WorkerListItem,
  WorkersListParams,
  Pagination,
  Skill,
} from '@/services/workersV2API';
import styled from '@/styles';

import WorkerFilterPanel, { FilterState } from './components/WorkerFilterPanel';
import WorkerCard from './components/WorkerCard';
import {
  expandBackgroundFilter,
  expandI9Filter,
  getBackgroundDisplayLabel,
  getI9DisplayLabel,
} from './components/StatusMappings';

const TABLE_HEADERS = [
  'Worker',
  'Onboarding',
  'I-9',
  'Background',
  'Rating',
  'Jobs',
  'Dependability',
  'Tax Type',
  'Status',
];

const ProgressBarOuter = styled('div', {
  width: 60,
  height: 6,
  backgroundColor: '$neutralA12',
  borderRadius: '$round',
  overflow: 'hidden',
  display: 'inline-block',
  verticalAlign: 'middle',
  marginLeft: 6,
});

const ProgressBarInner = styled('div', {
  height: '100%',
  borderRadius: '$round',
  variants: {
    color: {
      green: { backgroundColor: '#45A735' },
      yellow: { backgroundColor: '#F5A623' },
      gray: { backgroundColor: '#999999' },
    },
  },
  defaultVariants: {
    color: 'gray',
  },
});

const MobileList = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  padding: '12px',
});

const HideOnMobile = styled('td', {
  '@phoneOnly': {
    display: 'none',
  },
});

const dependabilityBadgeStatus: Record<
  string,
  'theme' | 'warning' | 'danger' | 'neutral'
> = {
  high: 'theme',
  medium: 'warning',
  low: 'danger',
  not_rated: 'neutral',
};

const i9BadgeStatus: Record<string, 'theme' | 'warning' | 'neutral'> = {
  Completed: 'theme',
  Pending: 'warning',
  'In Progress': 'warning',
  'Pending E-Verify': 'warning',
};

const bgBadgeStatus: Record<string, 'theme' | 'danger' | 'warning'> = {
  Approved: 'theme',
  Declined: 'danger',
  Pending: 'warning',
};

const getProgressColor = (pct: number): 'green' | 'yellow' | 'gray' => {
  if (pct >= 100) return 'green';
  if (pct >= 50) return 'yellow';
  return 'gray';
};

const EMPTY_FILTERS: FilterState = {};

const WorkersV2 = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [workers, setWorkers] = useState<WorkerListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Skills state
  const [skills, setSkills] = useState<Skill[]>([]);
  const [skillsLoading, setSkillsLoading] = useState(false);

  // Consolidated filter state
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Map skills to the format WorkerFilterPanel expects
  const skillsForPanel = useMemo(
    () => skills.map((s) => ({ id: s.id, name: s.display_name_en })),
    [skills],
  );

  // Load skills on mount
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        setSkillsLoading(true);
        const response = await WorkersV2API.getSkills();
        if (response.success) {
          setSkills(response.data);
        }
      } catch (err) {
        console.error('Error fetching skills:', err);
      } finally {
        setSkillsLoading(false);
      }
    };
    fetchSkills();
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search || '');
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const fetchWorkers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: WorkersListParams = {
        page: currentPage,
        per_page: itemsPerPage,
        sort_by: 'created_at',
        sort_direction: 'desc',
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.onboarding_status?.length)
        params.onboarding_status = filters.onboarding_status;
      if (filters.i9_status?.length)
        params.i9_status = expandI9Filter(filters.i9_status);
      if (filters.background_status?.length)
        params.checkr_status = expandBackgroundFilter(
          filters.background_status,
        );
      if (filters.tax_type) params.tax_type = filters.tax_type as 'w2' | '1099';
      if (filters.active) params.active = filters.active === 'true';
      if (filters.zip) params.zip = filters.zip;
      if (filters.radius) params.radius = parseFloat(filters.radius);
      if (filters.skill_ids?.length) params.skill_ids = filters.skill_ids;
      if (filters.dependability_tier?.length)
        params.dependability_tier = filters.dependability_tier;
      if (filters.accountability_status?.length)
        params.accountability_status = filters.accountability_status;

      const response = await WorkersV2API.getWorkers(params);

      if (response.success) {
        setWorkers(response.data);
        setPagination(response.pagination);
      } else {
        setError('Failed to fetch workers');
      }
    } catch (err) {
      console.error('Error fetching workers:', err);
      setError('Failed to fetch workers');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch, filters]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleNumberItemsChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setCurrentPage(1);
    setItemsPerPage(parseInt(event.target.value));
  };

  const handleWorkerClick = (workerId: number) => {
    navigate(`/workers_v2/${workerId}`);
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setCurrentPage(1);
  };

  const hasActiveFilters =
    filters.search ||
    filters.onboarding_status?.length ||
    filters.i9_status?.length ||
    filters.background_status?.length ||
    filters.tax_type ||
    filters.active ||
    filters.dependability_tier?.length ||
    filters.accountability_status?.length ||
    filters.skill_ids?.length ||
    filters.zip ||
    filters.radius;

  return (
    <Page size="full" title="Workers V2">
      <Card>
        <Card.Section>
          <WorkerFilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClear={handleClearFilters}
            skills={skillsForPanel}
          />
        </Card.Section>

        {loading ? (
          <Card.Section>
            <Stack style={{ justifyContent: 'center', padding: '40px' }}>
              <CircularProgress />
            </Stack>
          </Card.Section>
        ) : error ? (
          <Card.Section>
            <Body color="lighter">{error}</Body>
          </Card.Section>
        ) : workers.length === 0 ? (
          <Card.Section>
            <Stack vertical style={{ alignItems: 'center', padding: '40px' }}>
              <Body color="lighter">No workers found</Body>
              {hasActiveFilters && (
                <Small color="lighter">Try adjusting your filters</Small>
              )}
            </Stack>
          </Card.Section>
        ) : isMobile ? (
          <MobileList>
            {workers.map((worker) => (
              <WorkerCard
                key={worker.id}
                worker={worker}
                onClick={() => handleWorkerClick(worker.id)}
              />
            ))}
          </MobileList>
        ) : (
          <Table>
            <TableHeader fields={TABLE_HEADERS} />
            <tbody>
              {workers.map((worker) => {
                const i9Label = getI9DisplayLabel(worker.statuses.i9_status);
                const bgLabel = getBackgroundDisplayLabel(
                  worker.statuses.background_status,
                );
                const depTier = worker.dependability_tier || 'not_rated';
                const depLabel =
                  depTier.charAt(0).toUpperCase() +
                  depTier.slice(1).replace('_', ' ');

                return (
                  <tr key={worker.id}>
                    {/* Worker: Avatar + name + phone + email */}
                    <td style={{ padding: '12px 8px' }}>
                      <Stack gap={10}>
                        <Avatar
                          size="sm"
                          rounded
                          src={worker.avatar_url}
                          firstName={worker.user.first_name}
                          lastName={worker.user.last_name}
                          initialsFormat="firstLast"
                        />
                        <Stack vertical gap={2}>
                          <a
                            href={`/workers_v2/${worker.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#1976d2',
                              textDecoration: 'none',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Body weight="semibold">
                              {worker.user.full_name}
                            </Body>
                          </a>
                          {worker.user.phone_number && (
                            <Small color="lighter">
                              {worker.user.phone_number}
                            </Small>
                          )}
                          <Small color="lighter">{worker.user.email}</Small>
                        </Stack>
                      </Stack>
                    </td>

                    {/* Onboarding: % with progress bar */}
                    <td style={{ padding: '12px 8px' }}>
                      <Stack gap={4}>
                        <Small>{worker.onboarding_percentage}%</Small>
                        <ProgressBarOuter>
                          <ProgressBarInner
                            color={getProgressColor(
                              worker.onboarding_percentage,
                            )}
                            css={{
                              width: `${Math.min(worker.onboarding_percentage, 100)}%`,
                            }}
                          />
                        </ProgressBarOuter>
                      </Stack>
                    </td>

                    {/* I-9 */}
                    <td style={{ padding: '12px 8px' }}>
                      <Badge
                        label={i9Label}
                        status={i9BadgeStatus[i9Label] || 'neutral'}
                      />
                    </td>

                    {/* Background */}
                    <td style={{ padding: '12px 8px' }}>
                      <Badge
                        label={bgLabel}
                        status={bgBadgeStatus[bgLabel] || 'neutral'}
                      />
                    </td>

                    {/* Rating */}
                    <td style={{ padding: '12px 8px' }}>
                      <Small>
                        {worker.average_rating != null
                          ? `${worker.average_rating.toFixed(1)}`
                          : 'N/A'}
                      </Small>
                    </td>

                    {/* Jobs */}
                    <td style={{ padding: '12px 8px' }}>
                      <Small>{worker.jobs_worked}</Small>
                    </td>

                    {/* Dependability */}
                    <td style={{ padding: '12px 8px' }}>
                      <Badge
                        label={depLabel}
                        status={dependabilityBadgeStatus[depTier] || 'neutral'}
                      />
                    </td>

                    {/* Tax Type */}
                    <td style={{ padding: '12px 8px' }}>
                      <Badge
                        label={worker.tax_type.toUpperCase()}
                        status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
                      />
                    </td>

                    {/* Status */}
                    <td style={{ padding: '12px 8px' }}>
                      <Badge
                        label={worker.active ? 'Enabled' : 'Disabled'}
                        status={worker.active ? 'theme' : 'danger'}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {pagination && workers.length > 0 && (
        <Paginator
          currentPage={pagination.page}
          handleNumberItemsChange={handleNumberItemsChange}
          handlePageChange={handlePageChange}
          itemsLength={pagination.total}
          itemsPerPage={itemsPerPage}
        />
      )}
    </Page>
  );
};

export default WorkersV2;
